import React, { useMemo } from 'react';
import { Alert, Row, Col, Table } from 'react-bootstrap';

const MODULE_LABELS = {
  TIKANE: 'Ti Kanè',
  TIKANE_SENP: 'Ti Kanè Senp',
  ZELLE: 'Zelle',
  MONCASH: 'MonCash',
  NATCASH: 'NatCash',
  MICRO_CREDIT: 'Micro Crédit',
  EXCHANGE: 'Exchange System',
};

const USD_ENCAISSE_KEYS = ['EXCHANGE', 'MICRO_CREDIT'];
const HTG_ENCAISSE_KEYS = ['TIKANE', 'TIKANE_SENP', 'MONCASH', 'NATCASH', 'EXCHANGE', 'MICRO_CREDIT'];
const USD_DECAISSE_KEYS = ['ZELLE', 'EXCHANGE', 'MICRO_CREDIT'];
const HTG_DECAISSE_KEYS = ['TIKANE', 'TIKANE_SENP', 'MONCASH', 'NATCASH', 'ZELLE', 'EXCHANGE', 'MICRO_CREDIT'];

const fmt = (v) => Number(v || 0).toFixed(2);

const num = (v) => {
  const n = Number(v || 0);
  return Number.isFinite(n) ? n : 0;
};

function renderServiceAmounts(map = {}, keys = []) {
  return (
    <div className="small">
      {keys.map((key) => (
        <div key={key} className="d-flex justify-content-between border-bottom py-1 gap-2">
          <span>{MODULE_LABELS[key] || key}</span>
          <strong>{fmt(map?.[key])}</strong>
        </div>
      ))}
    </div>
  );
}

function renderFeeList(map = {}) {
  const rows = Object.entries(map).filter(([, amount]) => Number(amount || 0) > 0);

  if (!rows.length) {
    return <span className="text-muted">Aucun frais</span>;
  }

  return (
    <div className="small">
      {rows.map(([key, amount]) => (
        <div key={key} className="d-flex justify-content-between border-bottom py-1 gap-2">
          <span>{MODULE_LABELS[key] || key}</span>
          <strong>{fmt(amount)}</strong>
        </div>
      ))}
    </div>
  );
}

export default function CashSessionSummaryTable({ session, showRequesterClose = false }) {
  const breakdown = session?.breakdown || {
    encaisse: { USD: {}, HTG: {} },
    decaisse: { USD: {}, HTG: {} },
    fees: { USD: {}, HTG: {} },
  };

  const requester = session?.closeRequestPayload || null;

  const effective = useMemo(() => {
    if (!session) {
      return {
        openUsd: 0,
        openHtg: 0,
        systemBalanceUsd: 0,
        systemBalanceHtg: 0,
      };
    }

    const requests = Array.isArray(session.requests) ? session.requests : [];

    const openingRequest =
      requests.find((r) => r.requestType === 'OPEN' && r.status === 'APPROVED') ||
      requests.find((r) => r.requestType === 'OPEN');

    const openUsdFromSession = num(session.openUsd);
    const openHtgFromSession = num(session.openHtg);

    const openUsdFromRequest = num(
      openingRequest?.decisionPayload?.openUsd ?? openingRequest?.payload?.openUsd
    );
    const openHtgFromRequest = num(
      openingRequest?.decisionPayload?.openHtg ?? openingRequest?.payload?.openHtg
    );

    const effectiveOpenUsd = openUsdFromSession > 0 ? openUsdFromSession : openUsdFromRequest;
    const effectiveOpenHtg = openHtgFromSession > 0 ? openHtgFromSession : openHtgFromRequest;

    const apportUsd = num(session.apportUsd);
    const apportHtg = num(session.apportHtg);
    const retraitUsd = num(session.retraitUsd);
    const retraitHtg = num(session.retraitHtg);
    const totalEncaisseUsd = num(session.totalEncaisseUsd);
    const totalEncaisseHtg = num(session.totalEncaisseHtg);
    const totalDecaisseUsd = num(session.totalDecaisseUsd);
    const totalDecaisseHtg = num(session.totalDecaisseHtg);

    const effectiveSystemUsd =
      effectiveOpenUsd + apportUsd + totalEncaisseUsd - retraitUsd - totalDecaisseUsd;

    const effectiveSystemHtg =
      effectiveOpenHtg + apportHtg + totalEncaisseHtg - retraitHtg - totalDecaisseHtg;

    return {
      openUsd: effectiveOpenUsd,
      openHtg: effectiveOpenHtg,
      systemBalanceUsd: effectiveSystemUsd,
      systemBalanceHtg: effectiveSystemHtg,
    };
  }, [session]);

  if (!session) {
    return <Alert variant="info">Aucune caisse active.</Alert>;
  }

  return (
    <>
      <Table bordered size="sm" className="mb-3">
        <tbody>
          <tr>
            <th>Open USD</th>
            <td>{fmt(effective.openUsd)}</td>
            <th>Open HTG</th>
            <td>{fmt(effective.openHtg)}</td>
          </tr>
          <tr>
            <th>Apport USD</th>
            <td>{fmt(session.apportUsd)}</td>
            <th>Apport HTG</th>
            <td>{fmt(session.apportHtg)}</td>
          </tr>
          <tr>
            <th>Retrait USD</th>
            <td>{fmt(session.retraitUsd)}</td>
            <th>Retrait HTG</th>
            <td>{fmt(session.retraitHtg)}</td>
          </tr>
          <tr>
            <th>Total encaissé USD</th>
            <td>{fmt(session.totalEncaisseUsd)}</td>
            <th>Total encaissé HTG</th>
            <td>{fmt(session.totalEncaisseHtg)}</td>
          </tr>
          <tr>
            <th>Total décaissé USD</th>
            <td>{fmt(session.totalDecaisseUsd)}</td>
            <th>Total décaissé HTG</th>
            <td>{fmt(session.totalDecaisseHtg)}</td>
          </tr>
          <tr className="d-print-none">
            <th>Frais inclus USD (info)</th>
            <td>{fmt(session.totalFeesEncaisseUsd)}</td>
            <th>Frais inclus HTG (info)</th>
            <td>{fmt(session.totalFeesEncaisseHtg)}</td>
          </tr>
          <tr>
            <th>Solde système USD</th>
            <td>{fmt(effective.systemBalanceUsd)}</td>
            <th>Solde système HTG</th>
            <td>{fmt(effective.systemBalanceHtg)}</td>
          </tr>
          <tr>
            <th>Solde réel admin USD</th>
            <td>{session.realBalanceUsd == null ? '—' : fmt(session.realBalanceUsd)}</td>
            <th>Solde réel admin HTG</th>
            <td>{session.realBalanceHtg == null ? '—' : fmt(session.realBalanceHtg)}</td>
          </tr>
          <tr>
            <th>Écart admin USD</th>
            <td>{session.differenceUsd == null ? '—' : fmt(session.differenceUsd)}</td>
            <th>Écart admin HTG</th>
            <td>{session.differenceHtg == null ? '—' : fmt(session.differenceHtg)}</td>
          </tr>

          {showRequesterClose && requester && (
            <>
              <tr>
                <th>Solde réel utilisateur USD</th>
                <td>{fmt(requester.requesterRealBalanceUsd)}</td>
                <th>Solde réel utilisateur HTG</th>
                <td>{fmt(requester.requesterRealBalanceHtg)}</td>
              </tr>
              <tr>
                <th>Écart utilisateur USD</th>
                <td>{fmt(requester.requesterDifferenceUsd)}</td>
                <th>Écart utilisateur HTG</th>
                <td>{fmt(requester.requesterDifferenceHtg)}</td>
              </tr>
            </>
          )}
        </tbody>
      </Table>

      <Row className="g-3">
        <Col md={6}>
          <div className="border rounded p-2 h-100">
            <div className="fw-semibold mb-2">Encaissement USD</div>
            {renderServiceAmounts(breakdown.encaisse?.USD, USD_ENCAISSE_KEYS)}
          </div>
        </Col>

        <Col md={6}>
          <div className="border rounded p-2 h-100">
            <div className="fw-semibold mb-2">Encaissement HTG</div>
            {renderServiceAmounts(breakdown.encaisse?.HTG, HTG_ENCAISSE_KEYS)}
          </div>
        </Col>

        <Col md={6}>
          <div className="border rounded p-2 h-100">
            <div className="fw-semibold mb-2">Décaissement USD</div>
            {renderServiceAmounts(breakdown.decaisse?.USD, USD_DECAISSE_KEYS)}
          </div>
        </Col>

        <Col md={6}>
          <div className="border rounded p-2 h-100">
            <div className="fw-semibold mb-2">Décaissement HTG</div>
            {renderServiceAmounts(breakdown.decaisse?.HTG, HTG_DECAISSE_KEYS)}
          </div>
        </Col>

        <Col md={6}>
          <div className="border rounded p-2 h-100">
            <div className="fw-semibold mb-2">Frais encaissés USD</div>
            {renderFeeList(breakdown.fees?.USD)}
          </div>
        </Col>

        <Col md={6}>
          <div className="border rounded p-2 h-100">
            <div className="fw-semibold mb-2">Frais encaissés HTG</div>
            {renderFeeList(breakdown.fees?.HTG)}
          </div>
        </Col>
      </Row>
    </>
  );
}